import { AxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';

import {
  CustomerRequests,
  LoyaltyPointsTerms,
  LoyaltyPointsTransaction,
} from '~/api/Customer';
import { ApiErrorResponse } from '~/api/Requests';
import { company } from '~/company/Company';

import { BaseStore } from '../interfaces/BaseStore';
import { mainStore } from '../MainStore';
import { MetaStore } from '../shared/MetaStore';
import { UserStore } from '../UserStore';

export class LoyaltyPointsStore implements BaseStore {
  private readonly _userStore: UserStore;
  private readonly _abortController: AbortController = new AbortController();

  readonly gettingLoyaltyPointsMeta: MetaStore = new MetaStore();
  readonly gettingTransactionMeta: MetaStore = new MetaStore();
  readonly gettingTermsMeta: MetaStore = new MetaStore();

  showLoyaltyPoints: boolean | null = null;
  workWithPromocode: boolean | null = null;
  loyaltyPoints: number = 0;
  loyaltyPointsTransactionsList: LoyaltyPointsTransaction[] = [];
  terms: Record<string, number> = {
    rate: 0,
    paymentAmount: 0,
  };
  useLoyaltyPoints: boolean = false;
  loyaltyPointsAvailable: boolean = true;

  constructor(userStore: UserStore) {
    this._userStore = userStore;

    makeAutoObservable(this);
  }

  // Getters
  get showLoyaltyPointsOnFrontend() {
    return this.showLoyaltyPoints;
  }

  // Setters
  setUseLoyaltyPoints(val: boolean) {
    this.useLoyaltyPoints = val;
  }

  switchUseLoyaltyPoints() {
    this.useLoyaltyPoints = !this.useLoyaltyPoints;
  }

  setLoyaltyPoints(loyaltyPoints: number) {
    this.loyaltyPoints = loyaltyPoints;
  }

  setShowLoyaltyPoints(val: boolean | null) {
    this.showLoyaltyPoints = val;
  }

  setWorkWithPromocode(val: boolean | null) {
    this.workWithPromocode = val;
  }

  setTerms(terms: Record<string, number>) {
    this.terms = terms;
  }

  setLoyaltyPointsTransactionsList(list: LoyaltyPointsTransaction[]) {
    this.loyaltyPointsTransactionsList = list;
  }

  // Actions
  async requestLoyaltyPoints(): Promise<number> {
    if (!this._userStore.isAuthorized || company.isDisableLoyaltyProgram) {
      this.setLoyaltyPoints(0);

      return 0;
    }

    try {
      this.gettingLoyaltyPointsMeta.setLoading(true);
      const { data, status } = await CustomerRequests.requestLoyaltyPoints({
        signal: this._abortController.signal,
      });

      if (status === 'success') {
        this.setLoyaltyPoints(data);
      } else {
        this.setLoyaltyPoints(0);
      }

      return data;
    } catch (error: unknown) {
      this.setLoyaltyPoints(0);
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
      return 0;
    } finally {
      this.gettingLoyaltyPointsMeta.setLoading(false);
    }
  }

  async requestTerms(): Promise<LoyaltyPointsTerms[]> {
    try {
      this.gettingTransactionMeta.setLoading(true);
      const { data, status } = await CustomerRequests.requestLoyaltyPointsTerms(
        company.config.id,
      );

      if (status === 'success') {
        const showLoyaltyOnFrontend = data.find(
          (term) => term.configKey === 'showLoyaltyOnFrontend',
        );
        const rewardDefaultPercent = data.find(
          (term) => term.configKey === 'rewardDefaultPercent',
        );
        const payablePercent = data.find(
          (term) => term.configKey === 'payablePercent',
        );
        const allowLoyaltyWithPromocodes = data.find(
          (term) => term.configKey === 'allowLoyaltyWithPromocodes',
        );
        this.setTerms({
          rate: (rewardDefaultPercent?.configValue.value as number) || 0,
          paymentAmount: (payablePercent?.configValue.value as number) || 0,
        });
        this.setShowLoyaltyPoints(
          (showLoyaltyOnFrontend?.configValue.value as boolean) || false,
        );
        this.setWorkWithPromocode(
          (allowLoyaltyWithPromocodes?.configValue.value as boolean) || false,
        );
      } else {
        this.setTerms({
          rate: 0,
          paymentAmount: 0,
        });
        this.setShowLoyaltyPoints(false);
        this.setWorkWithPromocode(false);
      }

      return data;
    } catch (error: unknown) {
      this.setTerms({
        rate: 0,
        paymentAmount: 0,
      });
      this.setShowLoyaltyPoints(false);
      this.setWorkWithPromocode(false);
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
      return [];
    } finally {
      this.gettingTermsMeta.setLoading(false);
    }
  }

  async requestLoyaltyPointsTransactions(): Promise<
    LoyaltyPointsTransaction[]
  > {
    if (!this._userStore.isAuthorized || company.isDisableLoyaltyProgram) {
      this.setLoyaltyPointsTransactionsList([]);

      return [];
    }

    try {
      this.gettingTransactionMeta.setLoading(true);
      const { data, status } =
        await CustomerRequests.requestLoyaltyPointsTransactions(
          {
            'pagination[page]': 1,
            'pagination[size]': 20,
          },
          {
            signal: this._abortController.signal,
          },
        );

      if (status === 'success') {
        this.setLoyaltyPointsTransactionsList(data || []);
      } else {
        this.setLoyaltyPointsTransactionsList([]);
      }

      return data;
    } catch (error: unknown) {
      this.setLoyaltyPointsTransactionsList([]);
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
      return [];
    } finally {
      this.gettingTransactionMeta.setLoading(false);
    }
  }

  async getLoyaltyUserBalanceAndTransactions() {
    try {
      if (this._userStore.isAuthorized) {
        this.requestLoyaltyPointsTransactions();
        this.requestLoyaltyPoints();
      }
    } catch (error: unknown) {
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
    }
  }

  destroy() {
    this.gettingTermsMeta.destroy();
    this.gettingTransactionMeta.destroy();
    this._abortController.abort();
  }
}
